import { html } from "../../render";
import { attr, cn, safeRandomId, spreadAttrs, styleVars, } from "../../utils";
import { LpIcon } from "../icon";
export const LP_TOOLTIP_SIDES = ["top", "bottom", "left", "right"];
export const LP_TOOLTIP_ALIGNMENTS = ["start", "center", "end"];
export const LP_TOOLTIP_DEFAULTS = {
    side: "top",
    align: "center",
    delay: 600,
    closeDelay: 0,
    timeout: 400,
};
const tooltipManagedAttrs = [
    "data-lp-component",
    "class",
    "data-side",
    "data-align",
    "data-delay",
    "data-close-delay",
    "style",
];
const providerManagedAttrs = [
    "data-lp-component",
    "class",
    "data-delay",
    "data-close-delay",
    "data-timeout",
    "style",
];
function normalizeDelay(value) {
    return value === undefined ? undefined : Math.max(0, Math.floor(value));
}
function mergeStyle(variables, attributes) {
    const value = [
        styleVars(variables),
        typeof attributes?.style === "string" ? attributes.style : undefined,
    ].filter(Boolean).join(" ");
    return value || undefined;
}
export function LpTooltipProvider({ children, delay = LP_TOOLTIP_DEFAULTS.delay, closeDelay = LP_TOOLTIP_DEFAULTS.closeDelay, timeout = LP_TOOLTIP_DEFAULTS.timeout, className, attributes, }) {
    const normalizedDelay = normalizeDelay(delay) ?? LP_TOOLTIP_DEFAULTS.delay;
    const normalizedCloseDelay = normalizeDelay(closeDelay) ?? LP_TOOLTIP_DEFAULTS.closeDelay;
    const normalizedTimeout = normalizeDelay(timeout) ?? LP_TOOLTIP_DEFAULTS.timeout;
    const providerStyle = mergeStyle({
        "--lp-tooltip-delay": `${normalizedDelay}ms`,
        "--lp-tooltip-close-delay": `${normalizedCloseDelay}ms`,
    }, attributes);
    return html `
        <lp-tooltip-provider
            data-lp-component="TooltipProvider"
            class="${cn("lp-tooltip_provider", className, attributes?.class)}"
            data-delay="${normalizedDelay}"
            data-close-delay="${normalizedCloseDelay}"
            data-timeout="${normalizedTimeout}"
            ${attr("style", providerStyle)}
            ${spreadAttrs(attributes, { omit: providerManagedAttrs })}
        >
            ${children}
        </lp-tooltip-provider>
    `;
}
export function LpTooltip({ label, content, render, side = LP_TOOLTIP_DEFAULTS.side, align = LP_TOOLTIP_DEFAULTS.align, delay, closeDelay, className, attributes, }) {
    const id = safeRandomId();
    const popoverId = `lp-tooltip-popover-${id}`;
    const tooltipId = `lp-tooltip-${id}`;
    const normalizedDelay = normalizeDelay(delay);
    const normalizedCloseDelay = normalizeDelay(closeDelay);
    const tooltipStyle = mergeStyle({
        "--lp-tooltip-anchor-name": `--lp-tooltip-anchor-${id}`,
        "--lp-tooltip-delay": normalizedDelay === undefined
            ? undefined
            : `${normalizedDelay}ms`,
        "--lp-tooltip-close-delay": normalizedCloseDelay === undefined
            ? undefined
            : `${normalizedCloseDelay}ms`,
    }, attributes);
    const triggerAttributes = {
        class: "lp-tooltip_trigger",
        "data-lp-tooltip-id": tooltipId,
        interestfor: popoverId,
    };
    const trigger = render
        ? render({
            ariaLabel: label,
            attributes: triggerAttributes,
        })
        : html `
            <button
                class="lp-tooltip_trigger lp-tooltip_trigger-default"
                type="button"
                aria-label="${label}"
                data-lp-tooltip-id="${tooltipId}"
                interestfor="${popoverId}"
            >
                ${LpIcon({ name: "info", size: 14 })}
            </button>
        `;
    return html `
        <lp-tooltip
            data-lp-component="Tooltip"
            class="${cn("lp-tooltip", className, attributes?.class)}"
            data-side="${side}"
            data-align="${align}"
            ${attr("data-delay", normalizedDelay)}
            ${attr("data-close-delay", normalizedCloseDelay)}
            ${attr("style", tooltipStyle)}
            ${spreadAttrs(attributes, { omit: tooltipManagedAttrs })}
        >
            ${trigger}
            <div
                class="lp-tooltip_positioner"
                id="${popoverId}"
                popover="hint"
                data-side="${side}"
                data-align="${align}"
                hidden
            >
                <div class="lp-tooltip_popup" id="${tooltipId}" role="tooltip">
                    <div class="lp-tooltip_content">${content}</div>
                </div>
            </div>
        </lp-tooltip>
    `;
}
